/**
 * The Week / Month switch on the Overview's money widgets (Revenue Pulse,
 * Approved vs Invoiced). The choice is a per-viewer convenience kept in
 * localStorage under the caller's key; every accessor can throw in a private
 * window, so a failure falls back to the caller's default and stays in memory.
 */
import { useState } from 'react';

export type Period = 'week' | 'month';

function readPeriod(storageKey: string, fallback: Period): Period {
  try {
    const v = window.localStorage.getItem(storageKey);
    return v === 'month' || v === 'week' ? v : fallback;
  } catch {
    return fallback;
  }
}

export function usePeriod(storageKey: string, fallback: Period): [Period, (p: Period) => void] {
  const [period, setPeriod] = useState<Period>(() => readPeriod(storageKey, fallback));
  const choose = (p: Period) => {
    setPeriod(p);
    try { window.localStorage.setItem(storageKey, p); } catch { /* in-memory only */ }
  };
  return [period, choose];
}

export function PeriodToggle({ period, onChange }: { period: Period; onChange: (p: Period) => void }) {
  return (
    <div className="flex overflow-hidden rounded-fi-control border border-fieldiq-border" role="group" aria-label="Period">
      {(['week', 'month'] as const).map((p) => (
        <button
          key={p}
          type="button"
          aria-pressed={period === p}
          onClick={() => onChange(p)}
          className={`px-2.5 py-1 text-[10px] font-bold capitalize transition ${
            period === p ? 'bg-fi-signal-amber text-fi-signal-ink' : 'text-fieldiq-text'
          }`}
        >
          {p}
        </button>
      ))}
    </div>
  );
}
